"use client"

import { useRef, useState, type RefObject } from "react"
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@ui/shad"
import { $getSelection, $setSelection, type BaseSelection } from "lexical"
import { TextQuote } from "lucide-react"

import { useFetchEmailSnippets } from "@/hooks/api/email-snippets"
import { useLoadingRouter } from "@/hooks/use-loading-router"
import type { EditorRefHandle } from "@/components/lexical/editors/email"

import { $insertEmailSnippet } from "./insert-email-snippet"

export function EmailSnippetPicker({ editorRef }: { editorRef: RefObject<EditorRefHandle | null> }) {
  const [open, setOpen] = useState(false)
  const insertedRef = useRef(false)
  const selectionRef = useRef<BaseSelection | null>(null)
  const { data, isLoading, isError } = useFetchEmailSnippets()
  const router = useLoadingRouter({ bookScoped: false })
  const captureSelection = () => {
    editorRef.current
      ?.getEditor()
      ?.getEditorState()
      .read(() => {
        selectionRef.current = $getSelection()?.clone() ?? null
      })
  }
  return (
    <Popover
      open={open}
      onOpenChange={(value) => {
        if (value) {
          insertedRef.current = false
          captureSelection()
        }
        setOpen(value)
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          size="iconSm"
          variant="ghost"
          tooltip="Insert snippet"
          aria-label="Insert snippet"
          onMouseDown={(event) => {
            event.preventDefault()
            captureSelection()
          }}
        >
          <TextQuote />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-72 p-0"
        align="start"
        side="top"
        onCloseAutoFocus={(event) => {
          if (insertedRef.current) {
            event.preventDefault()
            editorRef.current?.focus()
          }
        }}
      >
        <Command>
          <CommandInput placeholder="Search snippets..." />
          <CommandList>
            <CommandEmpty>
              {isLoading ? "Loading snippets…" : isError ? "Unable to load snippets." : "No snippets found."}
            </CommandEmpty>
            <CommandGroup>
              {data?.email_snippets.map((snippet) => (
                <CommandItem
                  key={snippet.id}
                  value={snippet.id}
                  keywords={[snippet.name]}
                  onSelect={() => {
                    const editor = editorRef.current?.getEditor()
                    if (!editor) return
                    editor.update(
                      () => {
                        if (selectionRef.current) $setSelection(selectionRef.current.clone())
                        $insertEmailSnippet(snippet.body)
                      },
                      { tag: "history-push" }
                    )
                    insertedRef.current = true
                    setOpen(false)
                  }}
                >
                  {snippet.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
        <div className="border-t p-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setOpen(false)
              router.push("/settings/data-customizations/email-snippets")
            }}
          >
            Manage snippets
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
